/**
 * Keeps a broker relay's SignalR hub on the API task that serves REST (2026-09-25).
 *
 * WHY: CQG/Rithmic sessions live in ONE API task's memory, created by the connect POST (REST). The
 * browser relays the broker's frames (the logon answer included) up its relay HUB, so the hub must sit
 * on that same task. ALB stickiness pins REST via its cookie (`credentials: 'include'`, 2026-07-30), and a
 * hub WebSocket that opens while the cookie exists lands with REST. But a hub that (re)connects WITHOUT a
 * live cookie (a fresh load or a wake after an hour idle: the cookie lasts 1 h; a deploy) is routed on its
 * own, and a WebSocket keeps its task for life. Dev 2026-09-24: the Rithmic hub on task A, the connect POST
 * on task B, the login answer dropped on A, and B's session in Connecting until the user pressed Cancel.
 * 14 days of dev logs: 38% of hub starts after an hour idle split like this.
 *
 * WHAT: one REST probe (it obtains/refreshes the cookie AND its `X-Instance-Id` names REST's task), then
 * compare with the task the hub reported (`CqgServerInstance` / `RithmicServerInstance`). On a mismatch,
 * reconnect ONLY the hub: its new handshake carries the cookie, so it lands with REST. Bounded, and a
 * probe that fails or a hub that never reports leaves everything as it was.
 */
import { lastRestInstanceId } from '@core/api/serverInstance';

/** Hub moves per alignment before giving up (each is one WebSocket reconnect). */
export const HUB_AFFINITY_MAX_MOVES = 3;
/** How long to wait for the hub's `*ServerInstance` push after a (re)connect. */
export const HUB_INSTANCE_WAIT_MS = 3_000;

export interface HubAffinityPorts {
  /** One REST call: carries (or obtains) the ALB sticky cookie; the api client records its `X-Instance-Id`. */
  probeRest(): Promise<unknown>;
  /** The API task the CURRENT hub connection reported, waiting up to `timeoutMs` for the push; null if none. */
  hubInstance(timeoutMs: number): Promise<string | null>;
  /** Replace the hub connection ONLY (never the broker socket). */
  reconnectHub(): Promise<void>;
  log(level: 'info' | 'warn', message: string, detail?: string): void;
}

/** `aligned` = already together; `moved` = the hub was moved to REST's task; `unknown` = could not tell. */
export type HubAffinityOutcome = 'aligned' | 'moved' | 'unknown' | 'gave-up';

export async function alignHubWithRest(
  name: string,
  ports: HubAffinityPorts,
  reason: string,
): Promise<HubAffinityOutcome> {
  for (let moves = 0; ; moves++) {
    try {
      await ports.probeRest();
    } catch (err) {
      ports.log('info', `${name} hub-affinity probe failed - leaving the hub where it is`, `reason=${reason} error=${String(err)}`);
      return 'unknown';
    }
    const rest = lastRestInstanceId();
    const hub = await ports.hubInstance(HUB_INSTANCE_WAIT_MS);
    if (!rest || !hub) {
      ports.log('info', `${name} hub-affinity unknown - leaving the hub where it is`,
        `reason=${reason} hubTask=${hub ?? 'n/a'} restTask=${rest ?? 'n/a'}`);
      return 'unknown';
    }
    if (rest === hub) {
      if (moves > 0) ports.log('info', `${name} hub-affinity hub now on the REST task`, `reason=${reason} task=${hub} moves=${moves}`);
      return moves === 0 ? 'aligned' : 'moved';
    }
    if (moves >= HUB_AFFINITY_MAX_MOVES) {
      ports.log('warn', `${name} hub-affinity gave up`,
        `reason=${reason} hubTask=${hub} restTask=${rest} after ${moves} moves - relayed frames may not reach the session`);
      return 'gave-up';
    }
    ports.log('info', `${name} hub-affinity moving hub to the REST task`,
      `reason=${reason} hubTask=${hub} restTask=${rest} move=${moves + 1}/${HUB_AFFINITY_MAX_MOVES}`);
    await ports.reconnectHub();
  }
}
